import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import {
  Building2,
  Mail,
  User,
  Phone,
  AlertCircle,
  ShieldCheck,
  CheckCircle,
} from 'lucide-react';

export const SignupPage = () => {
  const navigate = useNavigate();
  const { registerCompanyAdmin, verifyOtp } = useAuth();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('register');
  const [email, setEmail] = useState('');

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    const result = await registerCompanyAdmin({
      fullName: data.fullName,
      email: data.email,
      phone: data.phone,
      companyName: data.companyName,
      legalName: data.legalName,
      companyCode: data.companyCode,
      gstNumber: data.gstNumber,
      panNumber: data.panNumber,
      logoUrl: data.logoUrl,
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      city: data.city,
      state: data.state,
      country: data.country,
      postalCode: data.postalCode,
    });

    if (result.success) {
      setEmail(data.email);
      setStep('verify-otp');
    } else {
      setError(Array.isArray(result.error) ? result.error.join(', ') : result.error);
    }

    setIsLoading(false);
  };

  const onVerifyOtpSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    const result = await verifyOtp(email, data.otp);

    if (result.success) {
      navigate('/pending-approval');
    } else {
      setError(result.error);
    }

    setIsLoading(false);
  };

  if (step === 'verify-otp') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <CheckCircle className="mx-auto mb-4 text-green-600" size={48} />
            <h1 className="text-3xl font-bold text-gray-900">Verify Signup</h1>
            <p className="text-gray-600 mt-2">We&apos;ve sent an OTP to {email}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-600 mt-0.5" size={20} />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onVerifyOtpSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OTP Code
              </label>
              <input
                type="text"
                {...register('otp', {
                  required: 'OTP is required',
                  pattern: {
                    value: /^[0-9]{6}$/,
                    message: 'OTP must be 6 digits',
                  },
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition text-center text-2xl tracking-widest"
                placeholder="000000"
                maxLength="6"
              />
              {errors.otp && (
                <p className="text-red-600 text-sm mt-1">{errors.otp.message}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 transition"
            >
              {isLoading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
          <div className="p-8 lg:p-10">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Register Company Admin</h1>
            <p className="text-gray-600 mb-8">
              Create the first company admin account and verify it with OTP before super admin approval.
            </p>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <AlertCircle className="text-red-600 mt-0.5" size={20} />
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="text"
                    {...register('fullName', { required: 'Admin name is required' })}
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Akram Khan"
                  />
                </div>
                {errors.fullName && <p className="text-red-600 text-sm mt-1">{errors.fullName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="email"
                    {...register('email', { required: 'Email is required' })}
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="admin@company.com"
                  />
                </div>
                {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="text"
                    {...register('phone')}
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="text"
                    {...register('companyName', { required: 'Company name is required' })}
                    className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Acme Infra Pvt Ltd"
                  />
                </div>
                {errors.companyName && <p className="text-red-600 text-sm mt-1">{errors.companyName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
                <input
                  type="text"
                  {...register('legalName')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Acme Infra Private Limited"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Code</label>
                <input
                  type="text"
                  {...register('companyCode', { required: 'Company code is required' })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ACME-01"
                />
                {errors.companyCode && <p className="text-red-600 text-sm mt-1">{errors.companyCode.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                <input
                  type="text"
                  {...register('gstNumber')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="27ABCDE1234F1Z5"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                <input
                  type="text"
                  {...register('panNumber')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="ABCDE1234F"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Logo Google Drive URL</label>
                <input
                  type="text"
                  {...register('logoUrl')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://drive.google.com/file/d/..."
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Address Line 1</label>
                <input
                  type="text"
                  {...register('addressLine1')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Street, building, area"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Address Line 2</label>
                <input
                  type="text"
                  {...register('addressLine2')}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Landmark or additional address"
                />
              </div>

              {[
                ['city', 'City'],
                ['state', 'State'],
                ['country', 'Country'],
                ['postalCode', 'Postal Code'],
              ].map(([name, label]) => (
                <div key={name}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input
                    type="text"
                    {...register(name)}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={label}
                  />
                </div>
              ))}

              <div className="md:col-span-2 pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium py-3 transition"
                >
                  {isLoading ? 'Sending OTP...' : 'Submit and Send OTP'}
                </button>
              </div>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200 text-sm text-gray-600">
              Already registered?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">
                Sign in
              </Link>
            </div>
          </div>

          <div className="bg-slate-900 text-white p-8 lg:p-10 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-sm mb-6">
                <ShieldCheck size={16} />
                Phase 1 Onboarding
              </div>
              <h2 className="text-2xl font-semibold mb-4">How this works</h2>
              <div className="space-y-4 text-sm text-slate-200">
                <p>1. Register the first company admin and company profile.</p>
                <p>2. Verify the signup with OTP.</p>
                <p>3. Super admin reviews and approves the company.</p>
                <p>4. After approval, the admin can invite the rest of the team.</p>
              </div>
            </div>
            <p className="text-sm text-slate-300 mt-8">
              Super admins are created manually and do not use this registration page.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

